// WHY: bridges the wire `Request` shape (bare-name actors, ISO timestamps,
// uppercase carrier state, storage-key attachments, photo-key arrays) to
// the richer view `RequestView` consumed by the existing List/Drawer/tabs.
// Synthesised UserRef and Attachment fields are placeholders for the
// reads-only slice; the next slice will replace them with real lookups
// (carrier/user catalog) once mutations come off the in-memory store.

import type {
  AuditEntry,
  ChatMessage,
  RequestListRow,
  RequestCarrierBid as WireBid,
  UserActorRef,
} from '@magimp/shared';
import { RequestCarrierState } from '@magimp/shared';
import type {
  ActorRef,
  Attachment,
  AttachmentKind,
  AuditEntryView,
  CarrierBid,
  CarrierBidQuote,
  CarrierBidState,
  ChatMessageView,
  RequestBoxItemView,
  RequestBoxView,
  RequestBoxVolumeModeView,
  RequestItemView,
  RequestView,
  UserRef,
} from './types.js';

export const CARRIER_STATE_MAP: Readonly<Record<RequestCarrierState, CarrierBidState>> = {
  [RequestCarrierState.PENDING]: 'pending',
  [RequestCarrierState.ANSWERED]: 'answered',
  [RequestCarrierState.REJECTED]: 'rejected',
};

// INVARIANT: party UserRef for `RequestView.manager` / `.logist`. The
// wire carries `managerId` / `logistId` UUIDs (#445), so id is a real
// identity. `role` is the closed UI-only `RoleKind` enum, defaulted to
// 'manager' because the party fields do not carry a wire role today;
// any UI gate that needs per-actor role reads it from the auth user
// (`detectRoleKind`), not from this view-side ref.
function partyToUserRef(id: string, name: string): UserRef {
  return { id, name, role: 'manager' };
}

// View-side projection of the wire `UserActorRef` for chat author /
// audit actor refs. Pipes the full `RoleRef` through so the shared
// `roleLabel` helper can resolve labels for system AND custom roles.
function wireActorToActorRef(by: UserActorRef): ActorRef {
  return { id: by.id, name: by.name, role: by.role };
}

export function isoToEpoch(iso: string): number {
  return Date.parse(iso);
}

// INVARIANT (#1051): wire `daysMin`/`daysMax` form an optional+nullable
//   transit range. Both null together (legacy/no value), or both
//   positive integers with `daysMin <= daysMax`; the view normalises
//   `undefined` to `null` on each end.
export function wireBidToQuote(b: WireBid): CarrierBidQuote {
  return {
    id: b.id,
    pricePerUnit: b.pricePerUnit,
    currency: b.currency,
    unit: b.unit,
    daysMin: b.daysMin ?? null,
    daysMax: b.daysMax ?? null,
    comment: b.comment,
    quotedAt: isoToEpoch(b.quotedAt),
    validityDays: b.validityDays,
    // WHY (#703): scope/scopeRefId are load-bearing in the view layer —
    //   `getCheapestBid` skips overrides, `getEffectiveBidFor` resolves
    //   the most-specific quote for a sub-resource. Drop them here and
    //   every adapted bid silently looks like an override of unknown
    //   target, defeating both helpers.
    scope: b.scope,
    scopeRefId: b.scopeRefId,
    createdAt: isoToEpoch(b.createdAt),
    updatedAt: isoToEpoch(b.updatedAt),
  };
}

const EXT_KIND_MAP: Readonly<Record<string, AttachmentKind>> = {
  pdf: 'pdf',
  xlsx: 'xlsx',
  docx: 'docx',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  webp: 'image',
  gif: 'image',
};

// INVARIANT: storage keys are `<scope>/<userUuid>/<objectUuid>` plus an
// optional extension. The wire does not carry name/size, so we derive a
// display name from the last path segment and a kind from the extension
// (defaulting to 'file'). size=0 is a known placeholder until the wire
// schema is extended with metadata.
function storageKeyToAttachment(key: string): Attachment {
  const segments = key.split('/');
  const last = segments[segments.length - 1] ?? key;
  const dot = last.lastIndexOf('.');
  const ext = dot >= 0 ? last.slice(dot + 1).toLowerCase() : '';
  return {
    id: key,
    name: last || key,
    size: 0,
    type: EXT_KIND_MAP[ext] ?? 'file',
  };
}

// WHY: `CarrierLookup` is a transitional extension point. No production
// caller currently passes `lookup`, and neither the request wire response
// nor the carrier catalog wire schema carries `code` / `country`, so the
// adapter falls back to `''` and the carrier badge UI derives initials from
// `name` (see `carrierMarkText`). The parameter and the `CarrierRef.code` /
// `CarrierRef.country` view fields are kept in place so a future explicit
// carrier-catalog linkage can populate the badge with a real short code
// without changing this signature.
export interface CarrierLookup {
  byId(id: string): { name: string; code: string; country: string } | null;
}

function wireBoxItemToView(it: {
  id: string;
  sortOrder: number;
  groupOrdinal: number;
  name: string;
  brand: string;
  // WHY (#1055): zod `optional()` infers `string | undefined`. Mirror
  //   that exactly so callers handing in the wire shape compose without
  //   an exactOptionalPropertyTypes mismatch.
  material?: string | undefined;
  description: string;
  quantity: number | null;
  photos: string[];
}): RequestBoxItemView {
  const view: RequestBoxItemView = {
    id: it.id,
    sortOrder: it.sortOrder,
    groupOrdinal: it.groupOrdinal,
    name: it.name,
    brand: it.brand,
    description: it.description,
    quantity: it.quantity,
    photoKeys: [...it.photos],
  };
  // WHY (#1055): only attach `material` when the wire emits it; the field
  //   is `exactOptionalPropertyTypes`-sensitive so an explicit `undefined`
  //   would fail under the project's strict TS config.
  if (it.material !== undefined) view.material = it.material;
  return view;
}

export function wireBoxToView(b: {
  id: string;
  sortOrder: number;
  count: number;
  weightKg: number;
  volumeM3: number;
  weightTotalKg: number;
  volumeTotalM3: number;
  volumeMode: RequestBoxVolumeModeView;
  widthCm: number | null;
  depthCm: number | null;
  heightCm: number | null;
  items: ReadonlyArray<{
    id: string;
    sortOrder: number;
    groupOrdinal: number;
    name: string;
    brand: string;
    material?: string | undefined;
    description: string;
    quantity: number | null;
    photos: string[];
  }>;
  updatedAt?: string | undefined;
}): RequestBoxView {
  const view: RequestBoxView = {
    id: b.id,
    sortOrder: b.sortOrder,
    count: b.count,
    weightKg: b.weightKg,
    volumeM3: b.volumeM3,
    weightTotalKg: b.weightTotalKg,
    volumeTotalM3: b.volumeTotalM3,
    volumeMode: b.volumeMode,
    widthCm: b.widthCm,
    depthCm: b.depthCm,
    heightCm: b.heightCm,
    items: b.items.map(wireBoxItemToView),
  };
  // WHY (#1178): `updatedAt` lands on the view only when the caller opts
  //   into `?includeBoxVersions=true`; legacy reads leave it undefined and
  //   the regroup UI falls back to read-only.
  if (b.updatedAt !== undefined) view.updatedAt = b.updatedAt;
  return view;
}

// WHY (#590/#1055/#1056): project one wire/draft request item onto the view
//   item shape (incl. the `photos` → `photoKeys` rename and the
//   exactOptionalPropertyTypes-safe conditional spreads). Extracted from
//   `wireRequestToView` so #2140's frozen-snapshot adapter reuses the exact
//   same projection for `DraftRequestItem[]` payload items.
export function wireItemToView(it: {
  id: string;
  name: string;
  brand: string;
  material?: string | undefined;
  weight: number | null;
  volume: number | null;
  quantity: number;
  productQuantity?: number | null | undefined;
  description: string;
  photos: string[];
  dimensionsMode?: boolean | undefined;
  packages?:
    | ReadonlyArray<{
        id: string;
        sortOrder: number;
        widthCm: number;
        depthCm: number;
        heightCm: number;
        weightKg: number;
        count: number;
      }>
    | undefined;
}): RequestItemView {
  const base: RequestItemView = {
    id: it.id,
    name: it.name,
    brand: it.brand,
    weight: it.weight,
    volume: it.volume,
    quantity: it.quantity,
    // WHY (#1056): wire field is optional+nullable. Coalesce `undefined`
    //   (legacy bundle never projected the key) to `null` so the view shape
    //   has a single absent sentinel.
    productQuantity: it.productQuantity ?? null,
    description: it.description,
    photoKeys: [...it.photos],
  };
  // WHY (#1055): only attach when the wire emits a string so legacy rows
  //   without the key parse cleanly under exactOptionalPropertyTypes.
  if (it.material !== undefined) base.material = it.material;
  // WHY (#590): expanded shape — keys land only when the backend was queried
  //   with `?includeItemPackages=true`. The detail query opts in; lists don't.
  if (it.dimensionsMode !== undefined) {
    base.dimensionsMode = it.dimensionsMode;
  }
  if (it.packages !== undefined) {
    base.packages = it.packages.map((p) => ({
      id: p.id,
      sortOrder: p.sortOrder,
      widthCm: p.widthCm,
      depthCm: p.depthCm,
      heightCm: p.heightCm,
      weightKg: p.weightKg,
      count: p.count,
    }));
  }
  return base;
}

// WHY (#1662): accept the list-row shape (chat/audit optional). Detail reads
//   pass a full `Request` — assignable to `RequestListRow` — so both the
//   drawer (full history) and the list/My-Day rows (history omitted) share
//   this projector. Absent chat/audit project to empty view arrays; the list
//   never renders them, and the drawer always fetches the full detail row.
export function wireRequestToView(req: RequestListRow, lookup?: CarrierLookup): RequestView {
  const view: RequestView = {
    id: req.id,
    name: req.name,
    status: req.status,
    // WHY (#1054): propagate the wire discriminator without transformation.
    //   Row/card components depend on `view.kind === 'CONTAINER'` to render
    //   the container badge; the China request list filter chips depend on
    //   it as the canonical client-side projection.
    kind: req.kind,
    // WHY (#1057): pass through the wire `deliveryAddress` field. The
    //   read drawer uses it as the route mirror for CONTAINER rows;
    //   the editor uses it as the controlled-input value.
    deliveryAddress: req.deliveryAddress,
    pickupAddress: req.pickupAddress,
    // WHY (#1061): pass-through projection of the insurance triplet so
    //   the read drawer + editor share one shape. The wire enforces
    //   the `false → both null` and `true → both non-null` invariants,
    //   so the view receives a coherent triplet without UI fallback
    //   logic.
    insuranceRequested: req.insuranceRequested,
    insuranceCargoValue: req.insuranceCargoValue,
    insuranceCargoCurrency: req.insuranceCargoCurrency,
    managerId: req.managerId,
    logistId: req.logistId,
    // WHY (#1597): pass through the funnel-link FKs so the drawer can build
    //   the chip navigation targets. `dealId` is wire-optional with a null
    //   default; coalesce to a single absent sentinel.
    leadId: req.leadId,
    dealId: req.dealId ?? null,
    manager: partyToUserRef(req.managerId, req.manager),
    logist: req.logist && req.logistId ? partyToUserRef(req.logistId, req.logist) : null,
    createdAt: isoToEpoch(req.createdAt),
    updatedAt: isoToEpoch(req.updatedAt),
    slaDeadline: req.slaDeadline ? isoToEpoch(req.slaDeadline) : null,
    inWorkAt: req.inWorkAt ? isoToEpoch(req.inWorkAt) : null,
    closedAt: req.closedAt ? isoToEpoch(req.closedAt) : null,
    processedAt: req.processedAt ? isoToEpoch(req.processedAt) : null,
    route: req.route,
    // WHY (#700): wire `destinations` carries the projected `ArrivalCity`
    //   block; pass it through unchanged so the editor + read surfaces
    //   share one shape.
    destinations: req.destinations.map((d) => ({
      arrivalCityId: d.arrivalCityId,
      sortOrder: d.sortOrder,
      arrivalCity: {
        id: d.arrivalCity.id,
        name: d.arrivalCity.name,
        shortName: d.arrivalCity.shortName,
        isDefault: d.arrivalCity.isDefault,
        active: d.arrivalCity.active,
      },
    })),
    // WHY (#996): pass-through projection of the request-level
    //   Roads/Packaging multiselects. Wire already orders by
    //   `(sortOrder asc, id asc)`; the view consumes the same array.
    roads: (req.roads ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      active: r.active,
      sortOrder: r.sortOrder,
    })),
    packaging: (req.packaging ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      active: p.active,
      sortOrder: p.sortOrder,
    })),
    weight: req.weight,
    volume: req.volume,
    items: req.items.map(wireItemToView),
    carriers: req.carriers.map((c): CarrierBid => {
      const ref = lookup?.byId(c.carrierId) ?? null;
      return {
        id: c.id,
        carrier: {
          id: c.carrierId,
          name: ref?.name ?? c.carrierName,
          code: ref?.code ?? '',
          country: ref?.country ?? '',
        },
        arrivalCityId: c.arrivalCityId,
        state: CARRIER_STATE_MAP[c.state],
        bids: c.bids.map(wireBidToQuote),
        // WHY (#1061/#1163): pass through the per-carrier insurance
        //   quote array. Wire is always an array (#1163 widened the
        //   single slot to "primary + overrides"); the wire layer
        //   guarantees order (primary first), so the view consumes it
        //   verbatim.
        insuranceQuotes: c.insuranceQuotes,
        rejectReason: c.rejectReason,
        createdAt: isoToEpoch(c.createdAt),
        updatedAt: isoToEpoch(c.updatedAt),
      };
    }),
    winnerBidId: req.winnerBidId,
    // WHY: wire carries storage-key strings; the view `Attachment`
    // requires `{name, size, type}`. We synthesise the display name from
    // the key's last path segment and infer the kind from its extension,
    // with size=0 as a documented placeholder until the wire schema
    // carries metadata. Preserves attachment presence so the UI can show
    // a per-key entry instead of erasing it.
    attachments: req.attachments.map(storageKeyToAttachment),
    chat: (req.chat ?? []).map(
      (m: ChatMessage): ChatMessageView => ({
        id: m.id,
        by: wireActorToActorRef(m.by),
        at: isoToEpoch(m.at),
        text: m.text,
        // INVARIANT (#570 + exactOptionalPropertyTypes): conditional spread
        //   so `undefined` never lands in the projected value.
        ...(m.readByCounterparty !== undefined ? { readByCounterparty: m.readByCounterparty } : {}),
      }),
    ),
    audit: (req.audit ?? []).map(
      (a: AuditEntry): AuditEntryView => ({
        at: isoToEpoch(a.at),
        by: a.by ? wireActorToActorRef(a.by) : null,
        text: a.text,
        // INVARIANT: with exactOptionalPropertyTypes, optional view fields
        // require key absence (not present-with-undefined), so wire
        // optional-undefined entries are spread-conditional here.
        ...(a.from !== undefined ? { from: a.from } : {}),
        ...(a.to !== undefined ? { to: a.to } : {}),
      }),
    ),
    returnReason: req.returnReason,
    refusalReason: req.refusalReason,
    refusalComment: req.refusalComment,
    archivedFromStatus: req.archivedFromStatus,
  };
  // WHY (#599): conditional spread — `boxes` lands only when the
  //   detail was fetched with `?includeBoxes=true`. Legacy/list
  //   reads omit the wire field; the view leaves it `undefined`
  //   so callers can branch on `view.boxes !== undefined`.
  if (req.boxes !== undefined) {
    view.boxes = req.boxes.map(wireBoxToView);
  }
  // WHY (#1597): funnel-link public codes are an opt-in expand
  //   (`?includeFunnelLinks=true`). Conditional spread so a default read
  //   that omits the keys leaves them absent (not present-with-undefined,
  //   per `exactOptionalPropertyTypes`); the chips render only when present.
  if (req.leadPublicCode !== undefined) {
    view.leadPublicCode = req.leadPublicCode;
  }
  if (req.dealPublicCode !== undefined) {
    view.dealPublicCode = req.dealPublicCode;
  }
  return view;
}
